import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { heroSlides } from "@/mocks/hero";
import { getMediaById, getCategoryName } from "@/utils/media";
import { useFavorites } from "@/hooks/useFavorites";

export default function HeroCarousel() {
  const [index, setIndex] = useState(0);
  const { isFavorite, toggleFavorite } = useFavorites();
  const total = heroSlides.length;

  const go = useCallback(
    (next: number) => {
      setIndex((next + total) % total);
    },
    [total],
  );

  useEffect(() => {
    const timer = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % total);
    }, 5500);
    return () => window.clearInterval(timer);
  }, [total]);

  const slide = heroSlides[index];
  const media = getMediaById(slide.id);
  if (!media) return null;
  const favorited = isFavorite(media.id);

  return (
    <section className="relative w-full">
      <div className="relative h-[420px] w-full overflow-hidden md:h-[560px]">
        {heroSlides.map((item, i) => (
          <div
            key={item.id}
            className={`absolute inset-0 transition-opacity duration-700 ${
              i === index ? "opacity-100" : "opacity-0"
            }`}
          >
            <img
              src={item.image}
              alt={`${getMediaById(item.id)?.title ?? "影视"} 焦点图`}
              className="h-full w-full object-top"
            />
          </div>
        ))}
        <div className="absolute inset-0 bg-gradient-to-r from-primary-950/95 via-primary-950/70 to-primary-950/20" />
        <div className="absolute inset-0 bg-gradient-to-t from-primary-950/80 via-transparent to-primary-950/40" />

        <Link
          to={`/detail/${media.id}`}
          aria-label={`查看 ${media.title} 详情`}
          className="absolute inset-0"
        />

        <div className="relative mx-auto flex h-full w-full items-center px-4 md:px-6">
          <div className="max-w-2xl">
            <span className="inline-flex items-center gap-2 rounded-full bg-accent-500 px-3 py-1 text-xs font-bold text-primary-950">
              <i className="ri-fire-fill" />
              {slide.tag}
            </span>
            <h1 className="mt-4 font-heading text-3xl font-bold leading-tight text-background-50 md:text-5xl">
              {media.title}
            </h1>
            <div className="mt-3 flex flex-wrap items-center gap-3 text-sm text-background-200">
              <span className="flex items-center gap-1 font-semibold text-accent-400">
                <i className="ri-star-fill" />
                {media.rating.toFixed(1)}
              </span>
              <span>{media.year}</span>
              <span className="rounded-md bg-background-50/15 px-2 py-0.5">{getCategoryName(media.type)}</span>
              <span>{media.region}</span>
              <span>{media.genres.join(" / ")}</span>
            </div>
            <p className="mt-4 line-clamp-2 max-w-xl text-sm leading-relaxed text-background-200 md:line-clamp-3 md:text-base">
              {media.summary}
            </p>
            <div className="mt-6 flex flex-wrap items-center gap-3">
              <Link
                to={`/detail/${media.id}`}
                className="flex items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-5 py-2.5 text-sm font-bold text-primary-950 transition-colors hover:bg-accent-400"
              >
                <i className="ri-play-fill text-lg" />
                立即观看
              </Link>
              <button
                type="button"
                onClick={() => toggleFavorite(media.id)}
                className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-100/40 bg-background-50/10 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-background-50/20"
              >
                <i className={favorited ? "ri-heart-3-fill text-accent-400" : "ri-heart-3-line"} />
                {favorited ? "已收藏" : "收藏"}
              </button>
            </div>
          </div>
        </div>

        <button
          type="button"
          onClick={() => go(index - 1)}
          aria-label="上一张"
          className="absolute left-3 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-primary-950/50 text-xl text-background-50 transition-colors hover:bg-accent-500 hover:text-primary-950 md:left-6"
        >
          <i className="ri-arrow-left-s-line" />
        </button>
        <button
          type="button"
          onClick={() => go(index + 1)}
          aria-label="下一张"
          className="absolute right-3 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-primary-950/50 text-xl text-background-50 transition-colors hover:bg-accent-500 hover:text-primary-950 md:right-6"
        >
          <i className="ri-arrow-right-s-line" />
        </button>

        <div className="absolute bottom-5 left-1/2 flex -translate-x-1/2 items-center gap-2">
          {heroSlides.map((item, i) => (
            <button
              key={item.id}
              type="button"
              onClick={() => setIndex(i)}
              aria-label={`第 ${i + 1} 张`}
              className={`h-2 rounded-full transition-all duration-300 ${
                i === index ? "w-7 bg-accent-500" : "w-2 bg-background-50/50 hover:bg-background-50/80"
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}